import "./style.css";
import {
  ACESFilmicToneMapping,
  AmbientLight,
  Color,
  DirectionalLight,
  Fog,
  Group,
  HemisphereLight,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { applyStageAtmosphere, loadDistrict } from "./district";
import { configureGeo, latLonToLocal, loadElevation } from "./geo";
import { palette } from "./style/toon";
import {
  createHuntMarkers,
  flyOffsetForStop,
  updateMarkerStyles,
} from "./hunt";
import { placeHeroProps } from "./heroes";
import { CameraTour } from "./tour";
import {
  createPhotorealHero,
  type PhotorealHeroHandle,
} from "./photorealHero";
import {
  tryLoadSplatHero,
  type SplatHeroHandle,
  type SplatManifest,
} from "./splatHero";
import {
  loadHeroManifest,
  tryLoadGlbHero,
  type GlbHeroHandle,
} from "./glbHero";
import { CTA_URL, HUNT_STOPS, ORIGIN, applyDevStreetViewPack, type HuntStop } from "./stops";
import { track } from "./analytics";
import type { RealityHandle } from "./reality";

const SHARE_URL = "https://placestage.pages.dev";
const SHARE_TEXT =
  "I walked the Old Québec Downtown Hunt on PlaceStage — a browser twin of the district.";

configureGeo({
  lat: ORIGIN.lat,
  lon: ORIGIN.lon,
  elevProfile: "quebec-cliff",
  reliefExaggeration: 1.0,
});

type Mode = "gate" | "hunting" | "complete";

const params = new URLSearchParams(window.location.search);
const wantMapOnly = params.has("maponly") || params.get("maponly") === "1";
const wantHunt = params.has("hunt") || params.get("hunt") === "1";
const wantTour = params.has("tour") || params.get("tour") === "1";
const wantPeek = params.has("peek") || params.get("peek") === "1";

const viewports = document.querySelector<HTMLDivElement>("#viewports")!;
const paneReality = document.querySelector<HTMLDivElement>("#pane-reality")!;
const canvas = document.querySelector<HTMLCanvasElement>("#c")!;
const canvasReality = document.querySelector<HTMLCanvasElement>("#c-reality")!;
const gate = document.querySelector<HTMLElement>("#gate")!;
const topbar = document.querySelector<HTMLElement>("#topbar")!;
const huntPanel = document.querySelector<HTMLElement>("#hunt-panel")!;
const legend = document.querySelector<HTMLElement>("#legend")!;
const tourBanner = document.querySelector<HTMLDivElement>("#tour-banner")!;
const tourCaption = document.querySelector<HTMLParagraphElement>("#tour-caption")!;
const heroBadge = document.querySelector<HTMLDivElement>("#hero-badge")!;
const stopsList = document.querySelector<HTMLOListElement>("#stops-list")!;
const progressPill = document.querySelector<HTMLDivElement>("#progress-pill")!;
const progressFill = document.querySelector<HTMLDivElement>("#progress-fill")!;
const huntStep = document.querySelector<HTMLParagraphElement>("#hunt-step")!;
const stopTitle = document.querySelector<HTMLHeadingElement>("#stop-title")!;
const stopBlurb = document.querySelector<HTMLParagraphElement>("#stop-blurb")!;
const hint = document.querySelector<HTMLParagraphElement>("#hint")!;
const btnStartHunt = document.querySelector<HTMLButtonElement>("#btn-start-hunt")!;
const btnPlayTour = document.querySelector<HTMLButtonElement>("#btn-play-tour")!;
const btnPeekGate = document.querySelector<HTMLButtonElement>("#btn-peek-photoreal")!;
const btnPeekPanel = document.querySelector<HTMLButtonElement>("#btn-peek-panel")!;
const btnTourTop = document.querySelector<HTMLButtonElement>("#btn-tour-top")!;
const btnTourStop = document.querySelector<HTMLButtonElement>("#btn-tour-stop")!;
const btnGateCta = document.querySelector<HTMLAnchorElement>("#btn-gate-cta")!;
const btnTopCta = document.querySelector<HTMLAnchorElement>("#btn-top-cta")!;
const btnFind = document.querySelector<HTMLButtonElement>("#btn-find")!;
const btnFly = document.querySelector<HTMLButtonElement>("#btn-fly")!;
const btnReality = document.querySelector<HTMLButtonElement>("#btn-reality")!;
const btnShare = document.querySelector<HTMLButtonElement>("#btn-share")!;
const btnRestart = document.querySelector<HTMLButtonElement>("#btn-restart")!;
const stopPhoto = document.querySelector<HTMLImageElement>("#stop-photo")!;
const angleStrip = document.querySelector<HTMLDivElement>("#angle-strip")!;
const credits = document.querySelector<HTMLElement>("#credits")!;
const loadState = document.querySelector<HTMLDivElement>("#load-state")!;
const completeModal = document.querySelector<HTMLDivElement>("#complete-modal")!;
const completeTime = document.querySelector<HTMLElement>("#complete-time")!;
const btnCta = document.querySelector<HTMLAnchorElement>("#btn-cta")!;
const btnCloseModal = document.querySelector<HTMLButtonElement>("#btn-close-modal")!;

const STORAGE_KEY = "placestage-hunt-oq";
const found = new Set<string>(
  JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]") as string[],
);
let selectedId: string | null = HUNT_STOPS[0]?.id ?? null;
let mode: Mode = "gate";
let reality: RealityHandle | null = null;
let compareOn = false;
let activePhoto = HUNT_STOPS[0]?.photo ?? "";
let huntStartedAt: number | null = null;
let twinReady = false;
let tour: CameraTour | null = null;
let photoreal: PhotorealHeroHandle | null = null;
let splatHero: SplatHeroHandle | null = null;
let glbHero: GlbHeroHandle | null = null;
let chateauProcedural: Group | null = null;
let districtRoot: Group | null = null;

if (wantMapOnly) document.body.classList.add("maponly");

const scene = new Scene();
// Firewatch haze: closer fog, warm sky (palette defaults)
applyStageAtmosphere(
  scene as Scene & { background: Color; fog: Fog | null },
  140,
  780,
);

const renderer = new WebGLRenderer({
  canvas,
  antialias: true,
  powerPreference: "high-performance",
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.toneMapping = ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.12;

const camera = new PerspectiveCamera(52, 1, 0.5, 4000);
camera.position.set(95, 85, 210);

const controls = new OrbitControls(camera, canvas);
controls.enableDamping = true;
controls.dampingFactor = 0.07;
controls.minDistance = 25;
controls.maxDistance = 900;
controls.maxPolarAngle = Math.PI * 0.48;
controls.target.set(0, 12, 0);

scene.add(new AmbientLight(palette.hemiSky, 0.48));
scene.add(new HemisphereLight(palette.hemiSky, palette.hemiGround, 0.85));
const sun = new DirectionalLight(palette.sun, 1.15);
sun.position.set(120, 90, 30); // low late-day sun
scene.add(sun);

const markersRoot = new Group();
scene.add(markersRoot);
let markers = createHuntMarkers(HUNT_STOPS);
for (const m of markers) markersRoot.add(m.group);

function rebuildMarkers() {
  for (const m of markers) markersRoot.remove(m.group);
  markers = createHuntMarkers(HUNT_STOPS);
  for (const m of markers) markersRoot.add(m.group);
}

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...found]));
}

function stopIndex(id: string | null): number {
  if (!id) return 0;
  const i = HUNT_STOPS.findIndex((s) => s.id === id);
  return i < 0 ? 0 : i;
}

function updateProgress() {
  const n = HUNT_STOPS.length;
  progressPill.textContent = `${found.size} / ${n}`;
  progressFill.style.width = `${(found.size / Math.max(1, n)) * 100}%`;
  const idx = stopIndex(selectedId);
  huntStep.textContent = `Stop ${idx + 1} of ${n}`;
}

function setMode(next: Mode) {
  mode = next;
  document.body.classList.toggle("mode-gate", next === "gate");
  document.body.classList.toggle("mode-hunting", next === "hunting");
  document.body.classList.toggle("mode-complete", next === "complete");

  if (next === "gate") {
    gate.hidden = false;
    gate.classList.remove("leaving");
    topbar.hidden = true;
    huntPanel.hidden = true;
    legend.hidden = true;
    completeModal.hidden = true;
  } else if (next === "hunting") {
    gate.classList.add("leaving");
    window.setTimeout(() => {
      gate.hidden = true;
    }, 420);
    topbar.hidden = wantMapOnly;
    huntPanel.hidden = wantMapOnly;
    legend.hidden = wantMapOnly;
    completeModal.hidden = true;
  } else {
    topbar.hidden = wantMapOnly;
    huntPanel.hidden = wantMapOnly;
    legend.hidden = wantMapOnly;
    completeModal.hidden = wantMapOnly;
  }
}

function resizeTwin() {
  const w = canvas.clientWidth || canvas.parentElement?.clientWidth || window.innerWidth;
  const h = canvas.clientHeight || canvas.parentElement?.clientHeight || window.innerHeight;
  camera.aspect = w / Math.max(1, h);
  camera.updateProjectionMatrix();
  renderer.setSize(w, h, false);
}

function syncStopPhoto(stop: HuntStop, photoUrl?: string) {
  activePhoto = photoUrl || stop.photo;
  stopPhoto.src = activePhoto;
  stopPhoto.alt = stop.name;
  stopPhoto.hidden = false;

  const angles = stop.photos?.length ? stop.photos : [stop.photo];
  angleStrip.innerHTML = "";
  if (angles.length <= 1) {
    angleStrip.hidden = true;
    return;
  }
  angleStrip.hidden = false;
  for (const url of angles) {
    const btn = document.createElement("button");
    btn.type = "button";
    if (url === activePhoto) btn.classList.add("active");
    const img = document.createElement("img");
    img.src = url;
    img.alt = `${stop.name} angle`;
    img.loading = "lazy";
    img.onerror = () => {
      btn.remove();
    };
    btn.appendChild(img);
    btn.addEventListener("click", () => {
      activePhoto = url;
      stopPhoto.src = url;
      for (const b of angleStrip.querySelectorAll("button")) b.classList.remove("active");
      btn.classList.add("active");
    });
    angleStrip.appendChild(btn);
  }
}

function renderStops() {
  stopsList.innerHTML = "";
  for (const [i, stop] of HUNT_STOPS.entries()) {
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.type = "button";
    btn.dataset.id = stop.id;
    if (stop.id === selectedId) btn.classList.add("active");
    if (found.has(stop.id)) btn.classList.add("found");
    btn.innerHTML = `
      <span class="num">${String(i + 1).padStart(2, "0")}</span>
      <span>${stop.name}</span>
      <span class="check">${found.has(stop.id) ? "✓" : ""}</span>
    `;
    btn.addEventListener("click", () => selectStop(stop, true));
    li.appendChild(btn);
    stopsList.appendChild(li);
  }
  updateFindButton();
}

function updateFindButton() {
  btnFind.disabled = !(selectedId && !found.has(selectedId) && mode === "hunting");
}

function flyToStop(stop: HuntStop, instant = false) {
  const elevBoost =
    stop.id === "chateau-frontenac" ? 28 : Math.max(6, stop.height * 0.4);
  const target = latLonToLocal(stop.lat, stop.lon, elevBoost);
  const offset = flyOffsetForStop(stop.id);
  const nextCam = target.clone().add(offset);

  const apply = (t: typeof target, c: typeof nextCam) => {
    controls.target.copy(t);
    camera.position.copy(c);
    controls.update();
    reality?.syncFrom(camera, controls.target);
  };

  if (instant) {
    apply(target, nextCam);
    return;
  }
  const fromTarget = controls.target.clone();
  const fromCam = camera.position.clone();
  const start = performance.now();
  const dur = 950;
  function step(now: number) {
    const u = Math.min(1, (now - start) / dur);
    const e = 1 - (1 - u) ** 3;
    const t = fromTarget.clone().lerp(target, e);
    const c = fromCam.clone().lerp(nextCam, e);
    apply(t, c);
    if (u < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function selectStop(stop: HuntStop, fly: boolean) {
  selectedId = stop.id;
  stopTitle.textContent = stop.name;
  stopBlurb.textContent = stop.blurb;
  renderStops();
  updateProgress();
  syncStopPhoto(stop);
  if (fly) flyToStop(stop);
  hint.textContent = stop.blurb;
}

function nextUnfound(): HuntStop | undefined {
  return HUNT_STOPS.find((s) => !found.has(s.id));
}

function elapsedMinutes(): string {
  if (!huntStartedAt) return "—";
  const mins = Math.max(1, Math.round((Date.now() - huntStartedAt) / 60_000));
  return String(mins);
}

function showComplete() {
  completeTime.textContent = elapsedMinutes();
  setMode("complete");
  track("hunt_finish", { district: "old-quebec", minutes: elapsedMinutes() });
}

function markFound() {
  if (mode !== "hunting") return;
  if (!selectedId || found.has(selectedId)) return;
  found.add(selectedId);
  persist();
  updateProgress();
  renderStops();
  track("stop_complete", { stop: selectedId, count: found.size });

  if (found.size >= HUNT_STOPS.length) {
    hint.textContent = "Hunt complete — you walked the core.";
    showComplete();
    return;
  }
  const next = nextUnfound();
  if (next) {
    hint.textContent = `Found ${found.size}/${HUNT_STOPS.length}. Next: ${next.name}`;
    selectStop(next, true);
  }
}

function startHunt() {
  if (!twinReady) {
    hint.textContent = "Still loading the twin…";
    return;
  }
  if (!huntStartedAt) huntStartedAt = Date.now();
  track("hunt_start", { district: "old-quebec", resumed: found.size > 0 });

  // Resume mid-hunt if they already have progress
  const start =
    (found.size > 0 && found.size < HUNT_STOPS.length && nextUnfound()) ||
    HUNT_STOPS[0];
  selectedId = start.id;
  setMode("hunting");
  selectStop(start, true);
  hint.textContent =
    found.size > 0 && found.size < HUNT_STOPS.length
      ? `Welcome back — ${found.size}/${HUNT_STOPS.length} done. Next: ${start.name}.`
      : `${start.blurb} Orbit, then Mark found.`;
}

function restartHunt() {
  found.clear();
  persist();
  huntStartedAt = Date.now();
  updateProgress();
  selectedId = HUNT_STOPS[0].id;
  setMode("hunting");
  selectStop(HUNT_STOPS[0], true);
  hint.textContent = "Fresh hunt — start at the Château.";
  track("hunt_restart", { district: "old-quebec" });
}

function ensureTour(): CameraTour {
  if (!tour) {
    tour = new CameraTour({
      camera,
      controls,
      onCaption: (text, index, total) => {
        tourCaption.textContent = `${index + 1}/${total} · ${text}`;
      },
      onBeat: (stop) => {
        selectedId = stop.id;
        stopTitle.textContent = stop.name;
        stopBlurb.textContent = stop.blurb;
        renderStops();
        updateProgress();
        syncStopPhoto(stop);
      },
      onSync: () => reality?.syncFrom(camera, controls.target),
      onDone: () => {
        endTourUi(false);
        hint.textContent = "Tour done — Start Downtown Hunt to play the layer.";
        if (!wantMapOnly && mode === "gate") {
          /* stay on gate */
        } else if (mode !== "hunting") {
          setMode("gate");
          gate.hidden = false;
          gate.classList.remove("leaving");
        }
      },
    });
  }
  return tour;
}

function endTourUi(skipped: boolean) {
  document.body.classList.remove("touring");
  tourBanner.hidden = true;
  btnPlayTour.textContent = "Play 60s tour";
  btnTourTop.textContent = "Tour";
  btnPlayTour.disabled = !twinReady;
  btnTourTop.disabled = !twinReady;
  track(skipped ? "tour_skip" : "tour_finish", { district: "old-quebec" });
}

async function playTour(fromGate: boolean) {
  if (!twinReady) return;
  ensureTour().stop();
  document.body.classList.add("touring");
  tourBanner.hidden = false;
  btnPlayTour.disabled = true;
  btnTourTop.disabled = true;
  btnPlayTour.textContent = "Tour playing…";
  btnTourTop.textContent = "…";

  if (fromGate || mode === "gate") {
    gate.classList.add("leaving");
    window.setTimeout(() => {
      gate.hidden = true;
    }, 300);
    if (!wantMapOnly) {
      topbar.hidden = false;
      legend.hidden = false;
    }
  }

  track("tour_start", { district: "old-quebec", maponly: wantMapOnly });
  await ensureTour().play();
}

function stopTour() {
  ensureTour().stop();
  endTourUi(true);
  if (mode === "gate" || gate.classList.contains("leaving")) {
    setMode("gate");
    gate.hidden = false;
    gate.classList.remove("leaving");
    topbar.hidden = true;
    legend.hidden = true;
    huntPanel.hidden = true;
  }
  hint.textContent = "Tour skipped.";
}

function setPeekLabels(on: boolean) {
  const label = on ? "Hide photoreal Château" : "Peek photoreal Château";
  btnPeekGate.textContent = label;
  btnPeekPanel.textContent = label;
}

function setHeroBadge(text: string | null) {
  if (!text) {
    heroBadge.hidden = true;
    return;
  }
  heroBadge.textContent = text;
  heroBadge.hidden = false;
}

/** Hide stylized filler under the photoreal clip so brown Google roofs don't stack. */
function setDistrictNearChateauVisible(visible: boolean) {
  if (!districtRoot || !photoreal) return;
  const c = photoreal.clipCenter;
  const r = photoreal.clipRadius + 8;
  districtRoot.traverse((obj) => {
    if (!("isMesh" in obj) || !obj.isMesh) return;
    const kind = obj.userData?.kind;
    if (kind !== "building" && kind !== "tree") return;
    const x =
      typeof obj.userData.localX === "number" ? obj.userData.localX : obj.position.x;
    const z =
      typeof obj.userData.localZ === "number" ? obj.userData.localZ : obj.position.z;
    if (Math.hypot(x - c.x, z - c.z) < r) obj.visible = visible;
  });
}

async function togglePhotorealPeek(force?: boolean) {
  const key = import.meta.env.VITE_GOOGLE_MAP_TILES_API as string | undefined;
  if (!key) {
    hint.textContent = "Photoreal peek needs VITE_GOOGLE_MAP_TILES_API.";
    return;
  }

  const wantOn = force ?? !(photoreal?.enabled() ?? false);

  if (wantOn && !photoreal) {
    btnPeekGate.disabled = true;
    btnPeekPanel.disabled = true;
    btnPeekGate.textContent = "Loading photoreal…";
    btnPeekPanel.textContent = "Loading photoreal…";
    try {
      photoreal = await createPhotorealHero(key);
      scene.add(photoreal.group);
    } catch (err) {
      console.error(err);
      hint.textContent = "Could not load photoreal Château tiles.";
      setPeekLabels(false);
      btnPeekGate.disabled = false;
      btnPeekPanel.disabled = false;
      return;
    }
    btnPeekGate.disabled = false;
    btnPeekPanel.disabled = false;
  }

  if (!photoreal) return;

  photoreal.setEnabled(wantOn);
  if (chateauProcedural) {
    chateauProcedural.visible = !wantOn && !splatHero && !glbHero;
  }
  if (splatHero) splatHero.group.visible = !wantOn;
  if (glbHero) glbHero.group.visible = !wantOn;
  setDistrictNearChateauVisible(!wantOn);

  setPeekLabels(wantOn);
  setHeroBadge(
    wantOn
      ? "Photoreal hero · Google 3D Tiles · streamed · not cached"
      : splatHero
        ? "Owned Gaussian splat · Château Frontenac"
        : glbHero
          ? "Blender GLB · Château Frontenac"
          : chateauProcedural
            ? "Premium procedural · Château Frontenac"
            : null,
  );
  hint.textContent = wantOn
    ? "Peek on — photoreal Château only (twin filler hidden underneath)."
    : "Peek off — stylized twin.";
  track("photoreal_peek", { on: wantOn });

  if (wantOn) {
    const stop = HUNT_STOPS.find((s) => s.id === "chateau-frontenac");
    if (stop) flyToStop(stop, false);
  }
}

async function shareHunt() {
  track("share_click", { district: "old-quebec" });
  const shareLink = `${SHARE_URL}?peek=1&tour=1`;
  const payload = {
    title: "PlaceStage · Old Québec Hunt",
    text: SHARE_TEXT,
    url: shareLink,
  };
  try {
    if (navigator.share) {
      await navigator.share(payload);
      btnShare.textContent = "Shared — thanks";
      return;
    }
  } catch {
    /* user cancelled or unavailable — fall through */
  }
  try {
    await navigator.clipboard.writeText(`${SHARE_TEXT}\n${shareLink}`);
    btnShare.textContent = "Link copied";
  } catch {
    btnShare.textContent = shareLink;
  }
  window.setTimeout(() => {
    btnShare.textContent = "Share this hunt";
  }, 2200);
}

async function toggleCompare() {
  const key = import.meta.env.VITE_GOOGLE_MAP_TILES_API as string | undefined;
  if (!compareOn) {
    if (!key) {
      hint.textContent = "Compare needs VITE_GOOGLE_MAP_TILES_API.";
      return;
    }
    btnReality.disabled = true;
    btnReality.textContent = "Loading reality…";
    try {
      paneReality.hidden = false;
      viewports.classList.add("split");
      resizeTwin();
      const { enableRealityPeek } = await import("./reality");
      reality = await enableRealityPeek(canvasReality, key);
      compareOn = true;
      btnReality.textContent = "Hide reality compare";
      hint.textContent =
        "Side-by-side: orbit the twin (left) — Reality (right) mirrors it.";
      track("reality_compare", { on: true });
      const stop = HUNT_STOPS.find((s) => s.id === selectedId) || HUNT_STOPS[0];
      if (stop) flyToStop(stop, true);
      requestAnimationFrame(() => {
        resizeTwin();
        reality?.resize();
      });
    } catch (err) {
      console.error(err);
      paneReality.hidden = true;
      viewports.classList.remove("split");
      hint.textContent = "Could not load Reality pane.";
      btnReality.textContent = "Compare reality ▸";
      resizeTwin();
    } finally {
      btnReality.disabled = false;
    }
    return;
  }

  reality?.dispose();
  reality = null;
  compareOn = false;
  paneReality.hidden = true;
  viewports.classList.remove("split");
  btnReality.textContent = "Compare reality ▸";
  hint.textContent = "Compare closed — PlaceStage twin only.";
  track("reality_compare", { on: false });
  resizeTwin();
}

btnStartHunt.addEventListener("click", startHunt);
btnPlayTour.addEventListener("click", () => {
  void playTour(true);
});
btnTourTop.addEventListener("click", () => {
  void playTour(mode === "gate");
});
btnTourStop.addEventListener("click", stopTour);
btnPeekGate.addEventListener("click", () => {
  void togglePhotorealPeek();
});
btnPeekPanel.addEventListener("click", () => {
  void togglePhotorealPeek();
});
btnFind.addEventListener("click", markFound);
btnFly.addEventListener("click", () => {
  const stop =
    (selectedId && HUNT_STOPS.find((s) => s.id === selectedId)) ||
    nextUnfound() ||
    HUNT_STOPS[0];
  if (!stop) return;
  selectStop(stop, true);
});
btnReality.addEventListener("click", () => {
  void toggleCompare();
});
btnShare.addEventListener("click", () => {
  void shareHunt();
});
btnRestart.addEventListener("click", restartHunt);

for (const el of [btnCta, btnGateCta, btnTopCta]) {
  el.href = CTA_URL;
  el.addEventListener("click", () => track("cta_click", { dest: CTA_URL, surface: el.id }));
}

btnCloseModal.addEventListener("click", () => {
  completeModal.hidden = true;
  setMode("hunting");
  hint.textContent = "Hunt done — keep exploring the twin, or Play again.";
});

credits.textContent =
  "© OpenStreetMap · Ville de Québec · MRNF DEM · PlaceStage";

document.body.classList.add("mode-gate");
track("session_start", { district: "old-quebec", layer: "downtown-hunt" });
updateProgress();
renderStops();
syncStopPhoto(HUNT_STOPS[0]);
resizeTwin();

async function boot() {
  try {
    await loadElevation();
    const usedSv = await applyDevStreetViewPack(HUNT_STOPS);
    if (usedSv) rebuildMarkers();

    const { group, meta } = await loadDistrict();
    districtRoot = group;
    scene.add(group);
    for (const hero of placeHeroProps()) {
      scene.add(hero.group);
      if (hero.id === "chateau-frontenac") {
        chateauProcedural = hero.group;
        setHeroBadge("Premium procedural · Château Frontenac");
      }
    }

    credits.textContent = usedSv
      ? `${meta.attribution} · DEV Street View Static · PlaceStage`
      : `${meta.attribution} · PlaceStage`;

    const start = HUNT_STOPS[0];
    selectedId = start.id;
    stopTitle.textContent = start.name;
    stopBlurb.textContent = start.blurb;
    renderStops();
    syncStopPhoto(start);
    flyToStop(start, true);

    twinReady = true;
    btnStartHunt.disabled = false;
    btnPlayTour.disabled = false;
    btnTourTop.disabled = false;
    btnPeekGate.disabled = false;
    btnPeekPanel.disabled = false;
    if (found.size >= HUNT_STOPS.length) {
      btnStartHunt.textContent = "View completed hunt";
    } else if (found.size > 0) {
      btnStartHunt.textContent = `Continue hunt (${found.size}/8)`;
    } else {
      btnStartHunt.textContent = "Start Downtown Hunt";
    }

    // Unblock UI before optional heroes (must not hang on SPA HTML fallback)
    loadState.classList.add("hidden");
    resizeTwin();

    // Owned heroes — non-blocking. Priority: splat → GLB → procedural
    void (async () => {
      try {
        const manifest = await loadHeroManifest(
          "/heroes/chateau-frontenac/manifest.json",
        );
        if (!manifest) return;
        const stop = HUNT_STOPS.find((s) => s.id === "chateau-frontenac");
        if (!stop) return;

        if (manifest.splat?.enabled) {
          const splatSpec: SplatManifest = {
            id: manifest.id,
            ...manifest.splat,
            format: manifest.splat.format ?? "ksplat",
          };
          const loaded = await tryLoadSplatHero(splatSpec);
          if (loaded) {
            loaded.group.position.copy(latLonToLocal(stop.lat, stop.lon, 0));
            scene.add(loaded.group);
            splatHero = loaded;
            if (chateauProcedural) chateauProcedural.visible = false;
            if (glbHero) glbHero.group.visible = false;
            setHeroBadge(
              manifest.splat.attribution ??
                "Owned Gaussian splat · Château Frontenac",
            );
            console.info("[PlaceStage] splat hero loaded", manifest.splat.path);
            return;
          }
        }

        if (manifest.glb?.enabled) {
          const loaded = await tryLoadGlbHero(manifest.glb);
          if (loaded) {
            loaded.group.position.add(latLonToLocal(stop.lat, stop.lon, 0));
            scene.add(loaded.group);
            glbHero = loaded;
            if (chateauProcedural) chateauProcedural.visible = false;
            setHeroBadge(
              manifest.glb.attribution ?? "Blender GLB · Château Frontenac",
            );
            console.info("[PlaceStage] GLB hero loaded", manifest.glb.path);
          }
        }
      } catch (err) {
        console.warn("[PlaceStage] hero asset unavailable", err);
      }
    })();

    // URL deep-links (GeoLibre-style share params)
    if (wantPeek) {
      void togglePhotorealPeek(true);
    }
    if (wantTour) {
      void playTour(true);
    } else if (wantHunt) {
      startHunt();
    } else if (wantMapOnly) {
      setMode("hunting");
      huntPanel.hidden = true;
      topbar.hidden = true;
      legend.hidden = true;
      gate.hidden = true;
    }
  } catch (err) {
    console.error(err);
    loadState.textContent = "Could not load district data.";
  }
}

boot();

function onResize() {
  resizeTwin();
  reality?.resize();
}
window.addEventListener("resize", onResize);

const clock = { t: 0 };
function animate() {
  requestAnimationFrame(animate);
  clock.t += 0.016;
  controls.update();
  updateMarkerStyles(markers, selectedId, found, clock.t, camera);
  photoreal?.update(camera, renderer);
  splatHero?.update();
  if (compareOn && reality) {
    reality.syncFrom(camera, controls.target);
    reality.update();
  }
  renderer.render(scene, camera);
}
animate();
